import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { formatDate, projectInfo } from "@/components/shared";
import { FolderPlus, ChevronRight } from "lucide-react";

export default function CustomerProjectsPage() {
  const [projects, setProjects] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/customer/projects").then((r) => setProjects(r.data)).catch(() => setProjects([]));
  }, []);

  if (!projects) return <p className="text-slate-500 text-sm">Yükleniyor...</p>;

  return (
    <div className="space-y-8 fade-in max-w-4xl">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Müşteri Paneli</div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tighter mt-1" data-testid="customer-projects-title">Projelerim</h1>
        </div>
        <Link to="/musteri/proje-olustur" data-testid="customer-new-project-button">
          <Button className="rounded-none bg-slate-900 text-white hover:bg-slate-700 gap-2 font-semibold transition-colors duration-200">
            <FolderPlus size={15} /> Yeni Proje
          </Button>
        </Link>
      </div>

      {projects.length === 0 ? (
        <div className="border border-slate-200 bg-white p-10 text-center" data-testid="no-customer-projects">
          <p className="text-slate-500 text-sm">Henüz projeniz yok. İlk projenizi oluşturun, tedarikçi teklifleri toplansın.</p>
          <Link to="/musteri/proje-olustur">
            <Button className="rounded-none bg-slate-900 text-white hover:bg-slate-700 gap-2 mt-6 font-semibold transition-colors duration-200">
              <FolderPlus size={15} /> Proje Oluştur
            </Button>
          </Link>
        </div>
      ) : (
        <div className="border border-slate-200 bg-white">
          {projects.map((p) => (
            <button
              key={p.id}
              onClick={() => navigate(`/musteri/projeler/${p.id}`)}
              data-testid={`customer-project-row-${p.id}`}
              className="w-full text-left px-5 py-4 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 transition-colors duration-200 flex items-center justify-between gap-3"
            >
              <div className="min-w-0">
                <div className="text-sm font-semibold truncate flex items-center gap-2">
                  {p.name}
                  {p.unread_messages > 0 && (
                    <Badge className="rounded-none bg-blue-600 text-white text-[10px]" data-testid={`unread-messages-${p.id}`}>{p.unread_messages} yeni mesaj</Badge>
                  )}
                </div>
                <div className="text-xs text-slate-500 mt-0.5 truncate">{projectInfo(p)} · {formatDate(p.created_at)}</div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                {p.winner_company ? (
                  <Badge variant="outline" className="rounded-none text-xs bg-emerald-500/10 text-emerald-600 border-emerald-500/20" data-testid={`project-status-${p.id}`}>
                    Firma seçildi: {p.winner_company}
                  </Badge>
                ) : p.has_request ? (
                  <Badge variant="outline" className={`rounded-none text-xs ${p.offer_count > 0 ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/20" : "bg-yellow-500/10 text-yellow-600 border-yellow-500/20"}`} data-testid={`project-status-${p.id}`}>
                    {p.offer_count > 0 ? `${p.offer_count} teklif geldi` : "Teklif bekleniyor"}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="rounded-none text-xs bg-slate-100 text-slate-600 border-slate-300" data-testid={`project-status-${p.id}`}>İnceleniyor</Badge>
                )}
                <ChevronRight size={15} className="text-slate-400" />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
